import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from "@mui/icons-material/Search";
import BlockIcon from "@mui/icons-material/Block";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import RecordingCell from "../common/RecordingCell";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  TextField,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  CircularProgress,
  Tooltip,
  Grid,
  FormControlLabel,
  RadioGroup,
  Radio,
  useMediaQuery,
  Popover,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DataGrid,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  useGridApiRef,
} from "@mui/x-data-grid";

import { useDispatch, useSelector } from "react-redux";
import {
  createBlockReport,
  getBuyerDialog,
  getReport,
} from "../../redux/actions/reportAction";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { makeStyles } from "@mui/styles";
import dayjs from "dayjs";
import Datetime from "react-datetime";
import { getAdminUsersList } from "../../redux/actions/adminPortal_listAction";
import { StyledDataGrid } from "../../pages/CustomDataGrid";
import { callStatusMessages } from "../../pages/Tooltips";
import moment from "moment";
import useReportStyles from "../../user_portals/redirect_portal/report/styles/reportStyles";
import { exportModalDataToCSV } from "./utils/csvExporter";
import { ALL_BUYER_DIALOG_SUCCESS } from "../../redux/constants/reportConstants";
import PageTitle from "../common/PageTitle";
import AdminCdrDetailsModal from "./AdminCdrDetailsModal";
import {
  usePersistedColumnVisibility,
  usePersistedColumnWidths,
  usePersistedColumnOrder,
  usePersistedDensity,
  buildColumnVisibilityKey,
} from "../common/usePersistedColumnVisibility";

// Day.js plugins
dayjs.extend(require("dayjs/plugin/utc"));
dayjs.extend(require("dayjs/plugin/timezone"));

const drawerWidth = 240;

const useStyles = makeStyles({
  formControl: {
    "& .MuiInputBase-root": {
      color: "#666",
      borderColor: "transparent",
      borderWidth: "1px",
      borderStyle: "solid",
      height: "50px",
      minWidth: "120px",
      justifyContent: "center",
    },
    "& .MuiSelect-select.MuiSelect-select": {
      paddingRight: "0px",
    },
    "& .css-14s5rfu-MuiFormLabel-root-MuiInputLabel-root": {
      top: "-4px",
    },
  },
});

const theme = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "& .MuiDataGrid-row": {
            minHeight: "auto",
          },
        },
      },
      defaultProps: {
        density: "compact",
        exportButton: true,
      },
    },
  },
});

const CALL_STATUS_ARRAY = [
  "TFN_SUSPENDED",
  "SERVER_DOWN",
  "CALLER_ABANDONED",
  "CALL_TRANSFERED",
  "CALL_ANSWER",
  "TFN_USER_NOT_ACTIVE",
  "CALLERID_BLOCKED_BY_USER",
  "UNABLE_TO_JOIN_QUEUE",
  "DESTINATION_BUSY",
  "NOT_SUFFICIENT_FUNDS",
  "TFN_NOT_ACTIVE",
  "TRIED_ALL_CARRIER_NO_SUCCESS",
  "NORMAL_HANGUP",
  "DESTINATION_FAILED",
  "USER_NOT_FOUND",
  "TFN_USER_SUSPENDED",
  "NO_ANSWER",
  "CONGESTION",
  "DESTINATION_CONGESTION",
  "ANSWERED",
  "FASTAGI_DOWN",
];

// Helper functions
const getTodayWithMidnight = () => moment().startOf("day");
const getTodayWithEndOfDay = () => moment().endOf("day");

const formatDateTimeForAPI = (date) =>
  dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD HH:mm") : "";

const CallStatusTooltip = ({ statusKey }) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [anchorEl, setAnchorEl] = useState(null);

  const getStatusMessage = (key) => {
    const status = callStatusMessages.find((item) => item.key === key);
    return status ? status.value : "Unknown Status";
  };

  const handleClick = (event) => {
    if (isMobile) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const statusMessage = getStatusMessage(statusKey);

  return (
    <>
      {isMobile ? (
        <>
          <span
            onClick={handleClick}
            style={{
              color: "#1976d2",
              display: "block",
              whiteSpace: "nowrap",
              cursor: "pointer",
            }}
          >
            {statusKey}
          </span>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Typography sx={{ p: 2, maxWidth: "200px" }}>
              {statusMessage}
            </Typography>
          </Popover>
        </>
      ) : (
        <Tooltip title={statusMessage} arrow>
          <span
            style={{
              color: "#1976d2",
              display: "block",
              whiteSpace: "nowrap",
            }}
          >
            {statusKey}
          </span>
        </Tooltip>
      )}
    </>
  );
};

// Module-level, stable component reference so MUI DataGrid does NOT remount
// the toolbar subtree on every parent render. An inline `() => (...)` here
// would re-mount the search TextField on every keystroke and kill focus.
// Mirrors `ReportGridToolbar` in the user portal report.
const AdminGridToolbar = ({
  searchDestination,
  onSearchDestinationChange,
  alignMode,
  onAlignModeChange,
}) => (
  <GridToolbarContainer sx={{ gap: 1, p: 0.5 }}>
    <GridToolbarColumnsButton />
    <GridToolbarFilterButton />
    <GridToolbarDensitySelector />
    <GridToolbarExport />
    <Box sx={{ flexGrow: 1 }} />
    <TextField
      size="small"
      variant="outlined"
      placeholder="Search Caller ID / DID / Forwarded / Status"
      value={searchDestination || ""}
      onChange={(e) =>
        onSearchDestinationChange && onSearchDestinationChange(e.target.value)
      }
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: searchDestination ? (
          <InputAdornment position="end">
            <IconButton
              edge="end"
              size="small"
              onClick={() =>
                onSearchDestinationChange && onSearchDestinationChange("")
              }
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        width: 280,
        "& .MuiInputBase-root": {
          height: 32,
          fontSize: 13,
          color: "#666",
        },
      }}
    />
    <Box sx={{ flexGrow: 1 }} />
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <FormControl component="fieldset">
        <RadioGroup
          row
          value={alignMode}
          onChange={(e) => onAlignModeChange && onAlignModeChange(e.target.value)}
        >
          <FormControlLabel
            value="left"
            control={<Radio size="small" />}
            label="Align Left"
            sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
          />
          <FormControlLabel
            value="right"
            control={<Radio size="small" />}
            label="Align Right"
            sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }}
          />
        </RadioGroup>
      </FormControl>
    </Box>
  </GridToolbarContainer>
);

function AdminReport({ colorThem }) {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const classes = useStyles();
  const theme = useTheme();

  const adminToken = JSON.parse(localStorage.getItem("admin") || "null");
  const userRole = adminToken?.user_role;
  const isAdminOrSuper = userRole === "Admin" || userRole === "Superadmin" || userRole === "SuperAdmin";

  const apiRef = useGridApiRef();

  const audioRefs = useRef({});
  const [currentAudio, setCurrentAudio] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const prefKey = buildColumnVisibilityKey("admin");
  const [columnVisibilityModel, setColumnVisibilityModel] =
    usePersistedColumnVisibility(prefKey);
  const [savedWidths, handleColumnWidthChange] = usePersistedColumnWidths(prefKey);
  const { applyOrder, handleOrderChange } = usePersistedColumnOrder(prefKey, apiRef);
  const { density: savedDensity, handleStateChange: handleDensityStateChange } =
    usePersistedDensity(prefKey, "compact");
  const [openModal, setOpenModal] = useState(false);
  const [loader, setLoader] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [resp, setResp] = useState(null);
  const [searchDestination, setSearchDestination] = useState("");
  const [statusInfoOpen, setStatusInfoOpen] = useState(false);


  // Filter states
  const [filters, setFilters] = useState({
    userId: "",
    callDirection: "",
    didNumber: "",
    destination: "",
    status: "",
    callerId: "",
    extension: "",
    durationOp: ">=",
    durationValue: "",
    uniqueOnly: false,
  });

  const [radioValue, setRadioValue] = useState("new");
  const [alignMode, setAlignMode] = useState("left");
  const tableScrollRef = useRef(null);

  useEffect(() => {
    if (!tableScrollRef.current) return;
    const scroller = tableScrollRef.current.querySelector(".MuiDataGrid-virtualScroller");
    if (!scroller) return;
    // Fire twice — once immediately and once after canvas-sizing + layout
    // settles — so "Align Right" lands at the true rightmost position even
    // after columns get auto-resized on data load.
    const scrollToEdge = () => {
      if (alignMode === "right") {
        const maxLeft = scroller.scrollWidth - scroller.clientWidth;
        scroller.scrollLeft = Math.max(0, maxLeft);
      } else {
        scroller.scrollLeft = 0;
      }
    };
    const t1 = setTimeout(scrollToEdge, 0);
    const t2 = setTimeout(scrollToEdge, 250);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
    // Depending on `rows` here would be a TDZ crash — `rows` (const/useMemo)
    // is declared below this effect. The 250ms delayed scroll already lands
    // after the canvas-sizing pass that fires on row changes.
  }, [alignMode]);

  // Canvas column sizing effect is defined BELOW, after `rows` useMemo, so
  // it can read the transformed rows (mapped field names matching columns).
  const [toDate, setToDate] = useState(getTodayWithEndOfDay);
  const [fromDate, setFromDate] = useState(getTodayWithMidnight);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.between("sm"));

  const handleOpenModal = (row) => {
    let data = JSON.stringify({
      uniqueid: row.uniqueid,
    });
    setSelectedRow(row);
    //dispatch(getBuyerDialog(data));
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRow(null);
  };

  const formatDateTimeJSX = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return (
      <span style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <span className="cdr-date-part" style={{ fontSize: "13px" }}>
          {day}/{month}/{year}
        </span>
        <span className="cdr-time-part" style={{ fontSize: "13px" }}>
          {hours}:{minutes}:{seconds}
        </span>
      </span>
    );
  };

  // Memoized data transformation
  const rows = useMemo(() => {
    return (
      state?.report?.report?.data?.map((item, index) => ({
        id: index + 1,
        did_tfn: item.did_number,
        buyer_name: item.buyer_name,
        uniqueid: item.uniqueid,
        caller_id_number: item.src,
        user_uuid: item?.user_uuid,
        call_direction: item?.call_direction,
        disposition: item?.disposition,
        duration: item?.duration,
        billsec: item?.billsec,
        answer_at: item.answer_at,
        end_at: item.end_at,
        start_at: item.start_at,
        recording_path: item.recording_path,
        hangup_reason: item.status,
        call_status: item.call_status,
        campaign_name: item.campaign_name,
        destination: item.destination,
        username: item.username,
        answered_by: item.answered_by,
        transfered_to: item.transfered_to,
        forwarded_number: item.forwarded_number,
        used_minutes: item.used_minutes,
        incoming: item.incoming,
        outgoing: item.outgoing,
        hangupsource: item.hangupsource,
        hangupby: item.hangupby,
        user_id: item.user_id,
        call_result: item.call_result,
        line_type: item.line_type,
        carrier: item.carrier,
        location: item.location,
        country: item.country,
        country_code: item.country_code,
        outside_us: item.outside_us,
        fake_number: item.fake_number,
      })) || []
    );
  }, [state?.report?.report?.data]);

  // Client-side filters (duration + dedupe by unique id). Kept separate from
  // the server-side search payload so these don't require a backend round-trip.
  const filteredRows = useMemo(() => {
    const raw = filters.durationValue;
    const threshold =
      raw !== "" && !Number.isNaN(Number(raw)) ? Number(raw) : null;
    const op = filters.durationOp || ">=";

    const durationFiltered =
      threshold === null
        ? rows
        : rows.filter((row) => {
            const d = Number(row.duration);
            if (Number.isNaN(d)) return false;
            switch (op) {
              case "=":  return d === threshold;
              case "!=": return d !== threshold;
              case "<":  return d < threshold;
              case "<=": return d <= threshold;
              case ">":  return d > threshold;
              case ">=":
              default:   return d >= threshold;
            }
          });

    // Free-text search across the columns most likely to be searched —
    // case-insensitive substring match.
    const q = (searchDestination || "").trim().toLowerCase();
    const searchFiltered = !q
      ? durationFiltered
      : durationFiltered.filter((row) => {
          const fields = [
            row.caller_id_number,
            row.did_tfn,
            row.forwarded_number,
            row.call_result,
            row.uniqueid,
            row.hangup_reason,
          ];
          return fields.some(
            (v) => v != null && String(v).toLowerCase().includes(q)
          );
        });

    if (!filters.uniqueOnly) return searchFiltered;

    // Dedupe by uniqueid — first occurrence wins. Rows with empty uniqueid
    // pass through unchanged.
    const seen = new Set();
    const out = [];
    for (const row of searchFiltered) {
      const key = row.uniqueid;
      if (key == null || key === "") {
        out.push(row);
        continue;
      }
      if (!seen.has(key)) {
        seen.add(key);
        out.push(row);
      }
    }
    return out;
  }, [rows, filters.durationOp, filters.durationValue, filters.uniqueOnly, searchDestination]);

  // Canvas-based column sizing: measure header + every cell at the ACTUAL
  // rendered font (14px Roboto), add padding + buffer. Uses `rows` (the
  // transformed data with field names matching column definitions — the
  // raw state.report.report.data uses different names like src→caller_id_number).
  useEffect(() => {
    if (!apiRef.current || !rows?.length) return;
    const t = setTimeout(() => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        // Canvas measures at 14px — admin renderCells and headers now use
        // fixed fontSize: "14px" (normalized from calc() variants to match
        // the user portal approach). Measurement is exact.
        const headerFont = '700 14px "Roboto","Helvetica","Arial",sans-serif';
        const cellFont = '400 14px "Roboto","Helvetica","Arial",sans-serif';

        const minimums = {};

        // Widget / fixed-format columns — skip canvas measurement, use a
        // fixed width. The raw value's width differs from the rendered
        // output, so measuring the raw gives the wrong answer.
        //   - recording_path: URL raw, compact widget rendered
        //   - start_at/end_at/answer_at: ISO raw, "DD/MM/YYYY HH:MM:SS"
        //     rendered — known width, no per-row variation
        const fixedWidths = {
          recording_path: 140,
          start_at: 145,
          end_at: 145,
          answer_at: 145,
        };

        // IMPORTANT: spread full column def — updateColumns REPLACES entries
        // for listed fields. Passing only {field, width} wipes renderCell.
        const cols = apiRef.current.getAllColumns();
        const updates = cols.map((col) => {
          // MUI's built-in selection checkbox column — force a compact fixed
          // size and block resizing so it can't drift wider over time.
          if (col.field === "__check__") {
            return {
              ...col,
              width: 52,
              minWidth: 52,
              maxWidth: 52,
              resizable: false,
            };
          }
          // User's manually-resized width (persisted) wins over canvas/fixed.
          const savedW = savedWidths[col.field];
          if (savedW) {
            return {
              ...col,
              width: savedW,
              maxWidth: Math.max(savedW, 400),
            };
          }
          if (fixedWidths[col.field]) {
            return {
              ...col,
              width: fixedWidths[col.field],
              maxWidth: 400,
            };
          }

          ctx.font = headerFont;
          let maxW = ctx.measureText(String(col.headerName || "")).width;

          ctx.font = cellFont;
          for (const row of rows) {
            const raw = row?.[col.field];
            if (raw == null) continue;
            const w = ctx.measureText(String(raw)).width;
            if (w > maxW) maxW = w;
          }

          const baseMin = minimums[col.field] || 60;
          const computedWidth = Math.max(baseMin, Math.ceil(maxW) + 20);
          return {
            ...col,
            width: computedWidth,
            // Strip any hardcoded maxWidth from the column definition —
            // otherwise MUI silently caps our canvas-measured width (e.g.
            // answer_at column declared maxWidth: 90 which truncated a
            // 170px-wide "DD/MM/YYYY HH:MM:SS" value). Generous cap lets
            // columns grow while still bounding pathological cases.
            maxWidth: Math.max(computedWidth, 400),
          };
        });

        apiRef.current.updateColumns(updates);
      } catch (_) { /* grid not mounted yet */ }
    }, 100);
    return () => clearTimeout(t);
  }, [rows.length, savedWidths]);

  // Selected caller data calculation
  const selectedCallerData = useMemo(() => {
    const selectedCallerDataMap = new Map();

    selectedRows?.forEach((id) => {
      const selectedRow = rows.find((row) => row.id === id);
      if (selectedRow) {
        const userId = selectedRow.user_id;
        if (!selectedCallerDataMap.has(userId)) {
          selectedCallerDataMap.set(userId, {
            type: "CallerID",
            details: [],
            description: "Report",
            user_id: JSON.stringify(userId),
            is_active: true,
          });
        }
        const data = selectedCallerDataMap.get(userId);
        if (!data.details.includes(selectedRow.caller_id_number)) {
          data.details.push(selectedRow.caller_id_number);
        }
      }
    });

    return Array.from(selectedCallerDataMap.values());
  }, [selectedRows, rows]);

  // Initial data load
  useEffect(() => {
    const initialData = JSON.stringify({
      from_date: formatDateTimeForAPI(fromDate),
      to_date: formatDateTimeForAPI(toDate),
    });

    dispatch(getReport(initialData, setLoader));
    dispatch(getAdminUsersList());
  }, [dispatch]);

  // Users data transformation
  useMemo(() => {
    if (state?.getAdminUsersList?.userList) {
      const usersArray = Object.keys(state.getAdminUsersList.userList).map(
        (key) => ({
          user_id: key,
          username: state.getAdminUsersList.userList[key],
        }),
      );
      setUsers(usersArray);
    }
  }, [state?.getAdminUsersList?.userList]);

  // Search handler - now only called when radio button changes
  const handleSearch = useCallback(() => {
    const searchData = JSON.stringify({
      caller_id: filters.callerId,
      user_id: filters.userId,
      from_date: formatDateTimeForAPI(fromDate),
      to_date: formatDateTimeForAPI(toDate),
      call_direction: filters.callDirection,
      didnumber: filters.didNumber,
      forward_number: filters.destination,
      hangup_reason: filters.status,
      extension: filters.extension,
      request_type: radioValue,
    });

    setLoader(true);
    dispatch(getReport(searchData, setLoader));
  }, [filters, fromDate, toDate, radioValue, dispatch]);

  // Trigger search when radio value changes
  useEffect(() => {
    handleSearch();
  }, [radioValue, resp]);

  const handleReset = () => {
    setFromDate(getTodayWithMidnight());
    setToDate(getTodayWithEndOfDay());
    setFilters({
      userId: "",
      callDirection: "",
      didNumber: "",
      destination: "",
      status: "",
      callerId: "",
      extension: "",
      durationOp: ">=",
      durationValue: "",
      uniqueOnly: false,
    });
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleAudioClick = (audioSrc) => {
    const audio = audioRefs.current[audioSrc];
    if (currentAudio === audio) {
      if (audio.paused) {
        audio.play();
      } else {
        audio.pause();
      }
    } else {
      if (currentAudio) {
        currentAudio.pause();
      }
      setCurrentAudio(audio);
      audio.play();
    }
  };

  const handleBlockCallerIds = () => {
    dispatch(createBlockReport(selectedCallerData, setResp, setLoader, setSelectedRows));
   
  };

  const handleSelectionChange = (selection) => {
    setSelectedRows(selection);
  };

  const handleFromDateChange = (date) => {
    if (moment.isMoment(date)) {
      setFromDate(date);
    }
  };

  const handleToDateChange = (date) => {
    if (moment.isMoment(date)) {
      setToDate(date);
    }
  };

  const handleAudioPause = () => {
    //  setCurrentAudio(null);
  };

  // Columns configuration
  const columns = [
    {
      field: "start_at",
      headerName: "Call Start Time",
      headerClassName: "custom-header",
      width: 155,
      minWidth: 145,
      maxWidth: 170,
      headerAlign: "left",
      disableColumnMenu: true,
      sortable: false,
      align: "left",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Call Start
        </Typography>
      ),
      renderCell: (params) => {
        if (params.value !== null) {
          const date = new Date(params.value);
          var day = date.getUTCDate();
          var month = date.getUTCMonth() + 1; // Month starts from 0
          var year = date.getUTCFullYear();
          var hours = date.getUTCHours();
          var minutes = date.getUTCMinutes();
          var seconds = date.getUTCSeconds();

          // Formatting single-digit day/month with leading zero if needed
          day = (day < 10 ? "0" : "") + day;
          month = (month < 10 ? "0" : "") + month;

          // Formatting single-digit hours/minutes/seconds with leading zero if needed
          hours = (hours < 10 ? "0" : "") + hours;
          minutes = (minutes < 10 ? "0" : "") + minutes;
          seconds = (seconds < 10 ? "0" : "") + seconds;
          return (
            <>
              <div style={{ display: "flex", flexDirection: "row", gap: "6px", whiteSpace: "nowrap", alignItems: "center" }}>
                <span
                  className="cdr-date-part"
                  style={{ fontSize: "14px" }}
                >
                  {`${day}/${month}/${year}`}
                </span>
                <span
                  className="cdr-time-part"
                  style={{ fontSize: "14px" }}
                >
                  {`${hours}:${minutes}:${seconds}`}
                </span>
              </div>
            </>
          );
        }
      },
    },
    {
      field: "username",
      headerName: "Username",
      headerClassName: "custom-header",
      width: isXs ? 120 : 120,
      minWidth: 120,
      maxWidth: 120,
      headerAlign: "start",
      align: "center",
      cellClassName: "super-app-theme--cell",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Username
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "caller_id_number",
      headerName: "Caller ID",
      headerClassName: "custom-header",
      width: 110,
      minWidth: 100,
      maxWidth: 120,
      headerAlign: "start",
      align: "start",
      cellClassName: "super-app-theme--cell",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Caller ID
        </Typography>
      ),
      renderCell: (params) => (
        <span
          onClick={() => handleOpenModal(params.row)}
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#2563eb",
            cursor: "pointer",
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "did_tfn",
      headerName: "DID Number",
      width: 110,
      minWidth: 100,
      maxWidth: 120,
      headerClassName: "custom-header",
      headerAlign: "start",
      align: "start",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          DID Number
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "forwarded_number",
      headerName: "Forwarded Number",
      width: 110,
      minWidth: 100,
      maxWidth: 120,
      headerClassName: "custom-header",
      headerAlign: "start",
      align: "start",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Forwarded
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "hangup_reason",
      headerName: "Status",
      minWidth: 150,
      headerAlign: "start",
      align: "start",
      headerClassName: "custom-header",
      renderHeader: () => (
        <Stack direction="row" alignItems="center" spacing={0.25}>
          <Typography
            variant="body2"
            sx={{
              fontSize: "14px",
              fontWeight: "bold",
              color: "white !important",
            }}
          >
            Status
          </Typography>
          <Tooltip title="What do these statuses mean?" arrow>
            <IconButton
              size="small"
              onMouseDown={(e) => {
                // Stop MUI DataGrid from interpreting the click as a
                // column-sort / drag inside the header.
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                setStatusInfoOpen(true);
              }}
              sx={{ p: 0.25, color: "#fff" }}
              aria-label="Status codes info"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
      renderCell: (params) => (
        <div style={{ fontSize: "14px" }}>
          <CallStatusTooltip statusKey={params.value} />
        </div>
      ),
    },
    {
      field: "call_result",
      headerName: "Call Status",
      headerClassName: "custom-header",
      width: 80,
      minWidth: 75,
      maxWidth: 90,
      headerAlign: "left",
      disableColumnMenu: true,
      sortable: false,
      align: "left",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", fontWeight: "bold", color: "white !important" }}
        >
          Call Status
        </Typography>
      ),
      renderCell: (params) => (
        <span
          style={{
            color:
              params.row.call_result === "ANSWERED"
                ? "green"
                : params.row.call_result === "Missed"
                  ? "orange"
                  : params.row.call_result === "Failed"
                    ? "red"
                    : "red",
            fontSize: "14px",
          }}
        >
          {params.row.call_result}
        </span>
      ),
    },
    {
      field: "buyer_name",
      headerName: "Buyer Name",
      minWidth: 130,
      headerAlign: "start",
      align: "start",
      headerClassName: "custom-header",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Buyer Name
        </Typography>
      ),
      renderCell: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <span style={{ whiteSpace: "nowrap" }}>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "campaign_name",
      headerName: "Campaign Name",
      minWidth: 130,
      headerAlign: "start",
      align: "center",
      headerClassName: "custom-header",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Camp. Name
        </Typography>
      ),
      renderCell: (params) => (
        <Tooltip title={params.value || ""} arrow>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "duration",
      headerName: "Duration",
      width: isXs ? 45 : 50,
      minWidth: 45,
      maxWidth: 50,
      headerAlign: "start",
      align: "center",
      headerClassName: "custom-header",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Dura.
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "billsec",
      headerName: "Bill Sec",
      width: isXs ? 55 : 60,
      minWidth: 55,
      maxWidth: 60,
      headerAlign: "start",
      align: "center",
      headerClassName: "custom-header",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Bill Sec
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "used_minutes",
      headerName: "Used Minutes",
      width: isXs ? 80 : 90,
      minWidth: 80,
      maxWidth: 90,
      headerAlign: "start",
      align: "center",
      headerClassName: "custom-header",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Used Minutes
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },

    {
      field: "recording_path",
      headerName: "Recording",
      headerClassName: "custom-header",
      width: 95,
      minWidth: 88,
      maxWidth: 105,
      headerAlign: "left",
      disableColumnMenu: true,
      sortable: false,
      align: "center",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Recording
        </Typography>
      ),
      renderCell: (params) => (
        <RecordingCell src={params.row.recording_path} />
      ),
    },
    {
      field: "answer_at",
      headerName: "Call Answer Time",
      headerClassName: "custom-header",
      width: isXs ? 80 : 90,
      minWidth: 80,
      maxWidth: 90,
      headerAlign: "left",
      disableColumnMenu: true,
      sortable: false,
      align: "left",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Call Answer
        </Typography>
      ),
      //valueFormatter
      renderCell: (params) => {
        if (params.value !== null) {
          const date = new Date(params.value);
          var day = date.getUTCDate();
          var month = date.getUTCMonth() + 1; // Month starts from 0
          var year = date.getUTCFullYear();
          var hours = date.getUTCHours();
          var minutes = date.getUTCMinutes();
          var seconds = date.getUTCSeconds();

          // Formatting single-digit day/month with leading zero if needed
          day = (day < 10 ? "0" : "") + day;
          month = (month < 10 ? "0" : "") + month;

          // Formatting single-digit hours/minutes/seconds with leading zero if needed
          hours = (hours < 10 ? "0" : "") + hours;
          minutes = (minutes < 10 ? "0" : "") + minutes;
          seconds = (seconds < 10 ? "0" : "") + seconds;
          return (
            <>
              <div style={{ display: "flex", flexDirection: "row", gap: "6px", whiteSpace: "nowrap", alignItems: "center" }}>
                <span
                  className="cdr-date-part"
                  style={{ fontSize: "14px" }}
                >
                  {day}/{month}/{year}
                </span>
                <span
                  className="cdr-time-part"
                  style={{ fontSize: "14px" }}
                >
                  {hours}:{minutes}:{seconds}
                </span>
              </div>
            </>
          );
        }
      },
    },

    {
      field: "end_at",
      headerName: "Call End Time",
      headerClassName: "custom-header",
      width: 155,
      minWidth: 145,
      maxWidth: 170,
      headerAlign: "left",
      disableColumnMenu: true,
      sortable: false,
      align: "left",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Call End
        </Typography>
      ),
      renderCell: (params) => {
        if (params.value !== null) {
          const date = new Date(params.value);
          var day = date.getUTCDate();
          var month = date.getUTCMonth() + 1; // Month starts from 0
          var year = date.getUTCFullYear();
          var hours = date.getUTCHours();
          var minutes = date.getUTCMinutes();
          var seconds = date.getUTCSeconds();

          // Formatting single-digit day/month with leading zero if needed
          day = (day < 10 ? "0" : "") + day;
          month = (month < 10 ? "0" : "") + month;

          // Formatting single-digit hours/minutes/seconds with leading zero if needed
          hours = (hours < 10 ? "0" : "") + hours;
          minutes = (minutes < 10 ? "0" : "") + minutes;
          seconds = (seconds < 10 ? "0" : "") + seconds;

          return (
            <>
              <div style={{ display: "flex", flexDirection: "row", gap: "6px", whiteSpace: "nowrap", alignItems: "center" }}>
                <span
                  className="cdr-date-part"
                  style={{ fontSize: "14px" }}
                >
                  {`${day}/${month}/${year}`}
                </span>
                <span
                  className="cdr-time-part"
                  style={{ fontSize: "14px" }}
                >
                  {`${hours}:${minutes}:${seconds}`}
                </span>
              </div>
            </>
          );
        }
      },
    },
    {
      field: "hangupby",
      headerName: "Hangup By",
      width: isXs ? 80 : 80,
      minWidth: 80,
      maxWidth: 80,
      headerAlign: "center",
      align: "center",
      headerClassName: "custom-header",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Hangup By
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },

    {
      field: "incoming",
      headerName: "Incoming",

      minWidth: 150,

      flex: 1,
      headerAlign: "left",
      disableColumnMenu: true,
      sortable: false,
      align: "left",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Incoming
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "outgoing",
      headerName: "Outgoing",

      minWidth: 220,

      flex: 1,
      headerAlign: "left",
      disableColumnMenu: true,
      sortable: false,
      align: "left",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Outgoing
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "hangupsource",
      headerName: "Hangup Source",
      flex: 1,
      minWidth: 160,
      headerAlign: "center",
      align: "center",
      headerAlign: "left",
      disableColumnMenu: true,
      sortable: false,
      align: "left",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Hangup Source
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },

    {
      field: "uniqueid",
      headerName: "Unique Id",
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      headerAlign: "center",

      disableColumnMenu: true,
      sortable: false,

      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Unique Id
        </Typography>
      ),
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px" }} // Match header size or set your own
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "line_type",
      headerName: "Line Type",
      headerClassName: "custom-header",
      headerAlign: "start",
      align: "start",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", fontWeight: "bold", color: "white !important" }}
        >
          Line Type
        </Typography>
      ),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "carrier",
      headerName: "Carrier",
      headerClassName: "custom-header",
      headerAlign: "start",
      align: "start",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", fontWeight: "bold", color: "white !important" }}
        >
          Carrier
        </Typography>
      ),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "country",
      headerName: "Country",
      headerClassName: "custom-header",
      headerAlign: "start",
      align: "start",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", fontWeight: "bold", color: "white !important" }}
        >
          Country
        </Typography>
      ),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "country_code",
      headerName: "Country Code",
      headerClassName: "custom-header",
      headerAlign: "start",
      align: "start",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", fontWeight: "bold", color: "white !important" }}
        >
          Country Code
        </Typography>
      ),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "location",
      headerName: "Location",
      headerClassName: "custom-header",
      headerAlign: "start",
      align: "start",
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", fontWeight: "bold", color: "white !important" }}
        >
          Location
        </Typography>
      ),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: "14px" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "outside_us",
      headerName: "Outside US",
      headerClassName: "custom-header",
      headerAlign: "start",
      align: "start",
      sortable: true,
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", fontWeight: "bold", color: "white !important" }}
        >
          Outside US
        </Typography>
      ),
      renderCell: (params) => {
        const yes =
          params.value === 1 ||
          params.value === true ||
          params.value === "1";
        return (
          <Chip
            size="small"
            label={yes ? "Yes" : "No"}
            color={yes ? "success" : "default"}
            variant={yes ? "filled" : "outlined"}
            sx={{ fontSize: "12px", height: "22px" }}
          />
        );
      },
    },
    {
      field: "fake_number",
      headerName: "Fake Number",
      headerClassName: "custom-header",
      headerAlign: "start",
      align: "start",
      sortable: true,
      renderHeader: () => (
        <Typography
          variant="body2"
          sx={{ fontSize: "14px", fontWeight: "bold", color: "white !important" }}
        >
          Fake Number
        </Typography>
      ),
      renderCell: (params) => {
        const yes =
          params.value === 1 ||
          params.value === true ||
          params.value === "1";
        return (
          <Chip
            size="small"
            label={yes ? "Yes" : "No"}
            color={yes ? "error" : "default"}
            variant={yes ? "filled" : "outlined"}
            sx={{ fontSize: "12px", height: "22px" }}
          />
        );
      },
    },
  ];

  const orderedColumns = applyOrder(columns);

  //  const CallDetailsModal = ({
  //     selectedRow,
  //     buyerDialogs,
  //   }) => {
  //     const classes = useReportStyles();

  //     if (!selectedRow) return null;

  //   };

  const handleExport = () => {
    const filename = `call_details_${selectedRow.caller_id_number}_${new Date().toISOString().slice(0, 10)}.csv`;
    exportModalDataToCSV(
      selectedRow,
      state?.buyerDialog?.buyer_dialog?.data || [],
      filename,
    );
  };

  return (
    <div className={`App ${colorThem} `}>
      <div className="contant_box">
        <Box
          className="right_sidebox mobile_top_pddng"
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="tab-content" id="pills-tabContent">
                  <div
                    className="tab-pane fade show active"
                    id="pills-home"
                    role="tabpanel"
                  >
                    <Box sx={{ maxWidth: "1340px", mx: "auto", width: "100%" }}>
                    <div className="cntnt_title">
                      <Box
                        sx={{
                          // 3-column CSS grid on sm+ — title (left), dates
                          // (page-centred), New/Old radio (right). Below sm
                          // it stacks vertically via flex-wrap. Same pattern
                          // as user portal report title row.
                          display: { xs: "flex", sm: "grid" },
                          gridTemplateColumns: { sm: "1fr auto 1fr" },
                          alignItems: "center",
                          flexWrap: "wrap",
                          columnGap: 2,
                          rowGap: 1,
                        }}
                      >
                        <Box sx={{ justifySelf: { sm: "start" } }}>
                          <PageTitle title="Call Details Records" />
                        </Box>

                        {/* From / To pickers — page-centred middle column. */}
                        <Box
                          sx={{
                            justifySelf: { sm: "center" },
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: 145,
                              maxWidth: 185,
                            }}
                          >
                            <label
                              style={{
                                fontSize: "13px",
                                paddingRight: "6px",
                                fontWeight: 500,
                                minWidth: "35px",
                              }}
                            >
                              {isSmallScreen ? "From" : "From:"}
                            </label>
                            <Datetime
                              className="datefield_select frm_date"
                              value={fromDate}
                              onChange={handleFromDateChange}
                              dateFormat="DD/MM/YYYY"
                              timeFormat="HH:mm"
                            />
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: 145,
                              maxWidth: 185,
                            }}
                          >
                            <label
                              style={{
                                fontSize: "13px",
                                paddingRight: "6px",
                                fontWeight: 500,
                                minWidth: "35px",
                              }}
                            >
                              {isSmallScreen ? "To" : "To:"}
                            </label>
                            <Datetime
                              className="datefield_select new_input"
                              value={toDate}
                              label="To Date"
                              onChange={handleToDateChange}
                              dateFormat="DD/MM/YYYY"
                              timeFormat="HH:mm"
                            />
                          </Box>
                        </Box>

                        <Box sx={{ justifySelf: { sm: "end" } }}>
                          <FormControl component="fieldset">
                            <RadioGroup
                              row
                              value={radioValue}
                              onChange={(e) => setRadioValue(e.target.value)}
                            >
                              <Tooltip
                                title={`From ${dayjs(state?.report?.report?.new).format("DD-MM-YYYY")} to till date`}
                              >
                                <FormControlLabel value="new" control={<Radio size="small" />} label="New" />
                              </Tooltip>
                              <FormControlLabel value="old" control={<Radio size="small" />} label="Old" />
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      </Box>
                    </div>

                    <Box
                      className="cdr_filter_row"
                      sx={{
                        py: 1,
                      }}
                    >
                    <Box
                      sx={{
                        // Single centred row containing all filter widgets +
                        // action buttons. Wraps gracefully on narrow widths.
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.75,
                      }}
                    >
                      {/* User Filter */}
                      <Box sx={{ flex: "1 1 140px", minWidth: 130, maxWidth: 170 }}>
                        <FormControl
                          fullWidth
                          className={classes.formControl}
                        >
                          <InputLabel id="user-select-label">UserName</InputLabel>
                          <Select
                            labelId="user-select-label"
                            label="UserName"
                            value={filters.userId}
                            onChange={(e) => handleFilterChange("userId", e.target.value)}
                          >
                            {users.map((item) => (
                              <MenuItem key={item.user_id} value={item.user_id}>
                                {item.username}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {/* Caller ID Filter */}
                      <Box sx={{ flex: "1 1 125px", minWidth: 115, maxWidth: 150 }}>
                        <TextField
                          className={`${classes.formControl} textfield_select`}
                          fullWidth
                          type="text"
                          label="Caller Id"
                          variant="outlined"
                          value={filters.callerId}
                          onChange={(e) => handleFilterChange("callerId", e.target.value)}
                        />
                      </Box>

                      {/* DID Number Filter */}
                      <Box sx={{ flex: "1 1 125px", minWidth: 115, maxWidth: 150 }}>
                        <TextField
                          className={`${classes.formControl} textfield_select`}
                          fullWidth
                          type="text"
                          label="DID Number"
                          variant="outlined"
                          value={filters.didNumber}
                          onChange={(e) => handleFilterChange("didNumber", e.target.value)}
                        />
                      </Box>

                      {/* Forward Number Filter */}
                      <Box sx={{ flex: "1 1 125px", minWidth: 115, maxWidth: 150 }}>
                        <TextField
                          className={`${classes.formControl} textfield_select`}
                          fullWidth
                          type="text"
                          label="Forward Number"
                          variant="outlined"
                          value={filters.destination}
                          onChange={(e) => handleFilterChange("destination", e.target.value)}
                        />
                      </Box>

                      {/* Duration (operator + value) — client-side filter */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: "0 0 auto" }}>
                        <TextField
                          className="textfield_select"
                          size="small"
                          select
                          label="Op"
                          variant="outlined"
                          value={filters.durationOp || ">="}
                          onChange={(e) => handleFilterChange("durationOp", e.target.value)}
                          sx={{
                            width: 72,
                            "& .MuiInputBase-root": { minWidth: 0, height: 40, fontSize: 14 },
                            "& .MuiInputLabel-root": { fontSize: 14 },
                          }}
                        >
                          <MenuItem value="=">=</MenuItem>
                          <MenuItem value="!=">≠</MenuItem>
                          <MenuItem value="<">&lt;</MenuItem>
                          <MenuItem value="<=">≤</MenuItem>
                          <MenuItem value=">">&gt;</MenuItem>
                          <MenuItem value=">=">≥</MenuItem>
                        </TextField>
                        <TextField
                          className="textfield_select"
                          size="small"
                          type="number"
                          label="Sec"
                          variant="outlined"
                          value={filters.durationValue || ""}
                          onChange={(e) => handleFilterChange("durationValue", e.target.value)}
                          inputProps={{ min: 0, maxLength: 3 }}
                          sx={{
                            width: 88,
                            "& .MuiInputBase-root": { minWidth: 0, height: 40, fontSize: 14 },
                            "& .MuiInputLabel-root": { fontSize: 14 },
                          }}
                        />
                      </Box>

                      {/* Unique rows (dedupe by uniqueid) */}
                      <Box sx={{ display: "flex", alignItems: "center", flex: "0 0 auto", px: 0.5 }}>
                        <Tooltip title="Show only unique rows by Unique ID" arrow>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={!!filters.uniqueOnly}
                                onChange={(e) => handleFilterChange("uniqueOnly", e.target.checked)}
                              />
                            }
                            label="Unique"
                            sx={{
                              m: 0,
                              "& .MuiFormControlLabel-label": { fontSize: 13, fontWeight: 500 },
                            }}
                          />
                        </Tooltip>
                      </Box>

                      {/* Action Buttons — inline siblings of the filters so
                          they're part of the centred group. */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        <div style={{ display: "flex", gap: "6px" }}>
                          <Button
                            variant="contained"
                            onClick={handleSearch}
                            startIcon={<SearchIcon sx={{ fontSize: 26 }} />}
                            size="small"
                            sx={{
                              background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                              color: "#fff !important",
                              textTransform: "none",
                              fontSize: "13px !important",
                              fontWeight: 600,
                              height: 40,
                              padding: "4px 14px !important",
                              minWidth: 0,
                              whiteSpace: "nowrap",
                              borderRadius: "8px !important",
                              boxShadow: "0 4px 12px rgba(22, 163, 74, 0.35)",
                              transition: "transform 0.15s ease, box-shadow 0.15s ease",
                              "&:hover": {
                                background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                                transform: "translateY(-1px)",
                                boxShadow: "0 6px 16px rgba(22, 163, 74, 0.45)",
                              },
                            }}
                          >
                            Apply Filters
                          </Button>

                          <Button
                            variant="contained"
                            onClick={handleReset}
                            startIcon={<RestartAltIcon sx={{ fontSize: 26 }} />}
                            size="small"
                            sx={{
                              background: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
                              color: "#fff !important",
                              textTransform: "none",
                              fontSize: "13px !important",
                              fontWeight: 600,
                              height: 40,
                              padding: "4px 14px !important",
                              minWidth: 0,
                              whiteSpace: "nowrap",
                              borderRadius: "8px !important",
                              boxShadow: "0 4px 12px rgba(71, 85, 105, 0.35)",
                              transition: "transform 0.15s ease, box-shadow 0.15s ease",
                              "&:hover": {
                                background: "linear-gradient(135deg, #475569 0%, #334155 100%)",
                                transform: "translateY(-1px)",
                                boxShadow: "0 6px 16px rgba(71, 85, 105, 0.45)",
                              },
                            }}
                          >
                            Reset
                          </Button>

                          <Button
                            variant="contained"
                            disabled={selectedRows.length === 0}
                            onClick={handleBlockCallerIds}
                            startIcon={<BlockIcon sx={{ fontSize: 26 }} />}
                            size="small"
                            sx={{
                              background: selectedRows.length
                                ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                                : "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)",
                              color: "#fff !important",
                              textTransform: "none",
                              fontSize: "13px !important",
                              fontWeight: 600,
                              height: 40,
                              padding: "4px 14px !important",
                              minWidth: 0,
                              whiteSpace: "nowrap",
                              borderRadius: "8px !important",
                              boxShadow: selectedRows.length
                                ? "0 4px 12px rgba(220, 38, 38, 0.35)"
                                : "0 2px 6px rgba(100, 116, 139, 0.25)",
                              transition: "transform 0.15s ease, box-shadow 0.15s ease",
                              "&:hover": {
                                background: selectedRows.length
                                  ? "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)"
                                  : "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)",
                                transform: selectedRows.length ? "translateY(-1px)" : "none",
                                boxShadow: selectedRows.length
                                  ? "0 6px 16px rgba(220, 38, 38, 0.45)"
                                  : "0 2px 6px rgba(100, 116, 139, 0.25)",
                              },
                            }}
                          >
                            Block{selectedRows.length ? ` (${selectedRows.length})` : ""}
                          </Button>
                        </div>
                      </Box>
                    </Box>
                    </Box>

                    {/* Data Grid */}
                    {loader ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          width: "90%",
                          marginTop: "9rem",
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <ThemeProvider theme={theme}>
                        <div ref={tableScrollRef} style={{ height: "auto", width: "100%" }}>
                          <StyledDataGrid
                            apiRef={apiRef}
                            rows={filteredRows}
                            columns={orderedColumns}
                            initialState={{ density: savedDensity }}
                            onStateChange={handleDensityStateChange}
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={setColumnVisibilityModel}
                            onColumnWidthChange={handleColumnWidthChange}
                            onColumnOrderChange={handleOrderChange}
                            rowHeight={36}
                            columnHeaderHeight={36}
                            components={{ Toolbar: AdminGridToolbar }}
                            componentsProps={{
                              toolbar: {
                                searchDestination,
                                onSearchDestinationChange: setSearchDestination,
                                alignMode,
                                onAlignModeChange: setAlignMode,
                              },
                            }}
                            checkboxSelection
                            disableRowSelectionOnClick
                            rowSelectionModel={selectedRows}
                            onRowSelectionModelChange={handleSelectionChange}
                            sx={{
                              "& .MuiDataGrid-toolbarContainer": {
                                gap: "1px",
                              },
                              "& .MuiButton-root": {
                                fontSize: "calc(0.6rem + 0.4vw)",
                                minWidth: "unset",
                              },
                              "& .MuiSvgIcon-root": {
                                fontSize: "calc(0.6rem + 0.4vw)",
                              },
                              "& .MuiDataGrid-columnHeaders": {
                                position: "sticky",
                                top: 0,
                                zIndex: 99,
                                backgroundColor: "#fff",
                              },
                              // Kill only the ellipsis — keep per-cell font
                              // sizes/colors/bold/hyperlinks/flex layouts
                              // intact. Canvas-measured widths +40px buffer
                              // guarantee text fits without needing "...".
                              "& .MuiDataGrid-cell, & .MuiDataGrid-cellContent": {
                                textOverflow: "clip !important",
                              },
                              "& .MuiDataGrid-cell": {
                                paddingTop: "2px",
                                paddingBottom: "2px",
                              },
                              "& .MuiDataGrid-filler, & .MuiDataGrid-columnHeader--filler, & .MuiDataGrid-scrollbarFiller, & .MuiDataGrid-scrollbarFiller--header, & .MuiDataGrid-scrollbarFiller--borderTop, & .MuiDataGrid-cell--filler, & .MuiDataGrid-row--filler": {
                                display: "none !important",
                              },
                            }}
                          />
                        </div>
                      </ThemeProvider>
                    )}
                    </Box>

                    {!isAdminOrSuper && (
                    <Dialog
                      open={openModal}
                      onClose={handleCloseModal}
                      maxWidth="xl"
                      fullWidth
                     >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          justifyContent: "space-between",
                          gap: 2,
                          p: { xs: "10px 12px", sm: "2px 20px" },
                        }}
                      >
                        {/* Title */}
                        <Typography
                          className="extension_title"
                          sx={{
                            color: "#0a140e",
                            fontSize: { xs: 18, sm: 20 },
                            fontWeight: 600,
                            textAlign: { xs: "center", sm: "left" },
                            width: "100%",
                          }}
                        >
                          Call Details
                        </Typography>

                        {/* Actions */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: {
                              xs: "space-between",
                              sm: "flex-end",
                            },
                            alignItems: "center",
                            width: { xs: "100%", sm: "auto" },
                            gap: 1,
                          }}
                        >
                          <Button
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExport}
                            variant="outlined"
                            size="small"
                            sx={{
                              mr: { sm: 2 },
                              width: { xs: "100%", sm: "100%" },
                            }}
                            style={{
                              background: "#0a140e",
                              color: "#fff",
                              textTransform: "capitalize",
                              width: "130px",
                            }}
                          >
                            Export CSV
                          </Button>

                          <IconButton
                            onClick={handleCloseModal}
                            className="close_icon"
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Box>

                      <DialogContent dividers>
                        {selectedRow && (
                          <>
                            {/* 🔹 Summary Section */}

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={12} md={12}>
                                <Box sx={{ mt: 0, mb: 2 }}>
                                  <Grid item xs={12} md={12}>
                                    <Typography
                                      sx={{
                                        fontWeight: "600",
                                        fontSize: "18px",

                                        marginBottom: "10px",
                                      }}
                                    >
                                      Call Summary
                                    </Typography>
                                  </Grid>

                                  <Grid container spacing={1}>
                                    {[
                                      // COLUMN 1
                                      [
                                        {
                                          label: "Caller ID",
                                          value: selectedRow.caller_id_number,
                                        },
                                        {
                                          label: "Username",
                                          value: selectedRow.username,
                                        },
                                        {
                                          label: "DID Number",
                                          value: selectedRow.did_tfn,
                                        },
                                        {
                                          label: "Forwarded",
                                          value: selectedRow.forwarded_number,
                                        },
                                        {
                                          label: "Status",
                                          value: selectedRow.hangup_reason,
                                        },
                                        {
                                          label: "Call Status",
                                          value: selectedRow.call_result,
                                        },
                                        {
                                          label: "Buyer Name",
                                          value: selectedRow.buyer_name,
                                        },
                                      ],

                                      // COLUMN 2
                                      [
                                        {
                                          label: "Cmp. Name",
                                          value: selectedRow.campaign_name,
                                        },
                                        {
                                          label: "Duration",
                                          value: selectedRow.duration,
                                        },
                                        {
                                          label: "Call Answer",
                                          value: formatDateTimeJSX(
                                            selectedRow.answer_at,
                                          ),
                                        },
                                        {
                                          label: "Call Start",
                                          value: formatDateTimeJSX(
                                            selectedRow.start_at,
                                          ),
                                        },
                                        {
                                          label: "Incoming",
                                          value: selectedRow.incoming,
                                        },
                                        {
                                          label: "Outgoing",
                                          value: selectedRow.outgoing,
                                        },
                                        {
                                          label: "Bill Sec",
                                          value: selectedRow.billsec,
                                        },
                                      ],

                                      // COLUMN 3
                                      [
                                        {
                                          label: "Used Minutes",
                                          value: selectedRow.used_minutes,
                                        },
                                        {
                                          label: "Call End",
                                          value: formatDateTimeJSX(
                                            selectedRow.end_at,
                                          ),
                                        },
                                        {
                                          label: "Hangup By",
                                          value: selectedRow.hangupby,
                                        },
                                        {
                                          label: "Unique ID",
                                          value: selectedRow.uniqueid,
                                        },
                                        {
                                          label: "Recording",
                                          value: "recording",
                                        },
                                      ],
                                    ].map((column, colIndex) => (
                                      <Grid item xs={12} md={4} key={colIndex}>
                                        <table
                                          className="table mb-0 align-middle"
                                          style={{
                                            width: "100%",
                                            tableLayout: "fixed", // ✅ IMPORTANT
                                          }}
                                         >
                                          <tbody>
                                            {column.map((row, rowIndex) => (
                                              <tr key={rowIndex}>
                                                {/* ===== LABEL TD (40%) ===== */}
                                                <td
                                                  style={{
                                                    width: "40%",
                                                    border: "none",
                                                    fontSize: "13px",
                                                    padding: "7px",
                                                    fontWeight: 700,
                                                    color: "#07285d",
                                                    wordBreak: "break-word",
                                                    verticalAlign: "top",
                                                  }}
                                                >
                                                  {row.label}
                                                </td>

                                                {/* ===== VALUE TD (60%) ===== */}
                                                <td
                                                  style={{
                                                    width: "60%",
                                                    border: "none",
                                                    fontSize: "13px",
                                                    padding: "6px",
                                                    wordBreak: "break-word",
                                                  }}
                                                >
                                                  {row.label === "Recording" ? (
                                                    selectedRow.recording_path ? (
                                                      <audio
                                                        ref={(audio) =>
                                                          (audioRefs.current[
                                                            selectedRow.recording_path
                                                          ] = audio)
                                                        }
                                                        src={
                                                          selectedRow.recording_path
                                                        }
                                                        controls
                                                        controlsList="download"
                                                        onPlay={() =>
                                                          handleAudioClick(
                                                            selectedRow.recording_path,
                                                          )
                                                        }
                                                        onPause={
                                                          handleAudioPause
                                                        }
                                                        style={{
                                                          width: "100%", // ✅ responsive
                                                          maxWidth: "220px",
                                                          height: "28px",
                                                        }}
                                                      />
                                                    ) : (
                                                      "-"
                                                    )
                                                  ) : (
                                                    row.value || "-"
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </Grid>
                                    ))}
                                  </Grid>
                                </Box>

                                {/* <Box sx={{ mt: 4, mb: 2 }}>
                                  <Grid item xs={12} md={12}>
                                    <Typography
                                      sx={{
                                        fontWeight: "600",
                                        fontSize: "18px",

                                        marginBottom: "10px",
                                      }}
                                    >
                                      Buyer Dial Log
                                    </Typography>
                                  </Grid>

                                  <div
                                    className="table-responsive"
                                    style={{
                                      borderRadius: "12px",
                                      overflowX: "auto",
                                      maxWidth: "100%",
                                    }}
                                  >
                                    <table
                                      className="table mb-0 align-middle"
                                      style={{
                                        whiteSpace: "nowrap",
                                        tableLayout: "auto",
                                      }}
                                    >
                                      <thead>
                                        <tr>
                                          {[
                                            "Unique Id",
                                            "Buyer Name",
                                            "Buyer Number",
                                            "Actual Timeout",
                                            "Timeout",
                                            "Status",
                                            "Created At",
                                          ].map((item, i) => (
                                            <th
                                              key={i}
                                              style={{
                                                fontSize: "12px",
                                                color: "#0a140e",
                                                whiteSpace: "nowrap",
                                              }}
                                            >
                                              {item}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>

                                      <tbody>
                                        {state?.buyerDialog?.buyer_dialog?.data
                                          ?.length > 0 &&
                                          state?.buyerDialog?.buyer_dialog?.data?.map(
                                            (dialog, index) => (
                                              <tr
                                                style={{
                                                  fontSize: "13px",
                                                  borderRadius: "0px",
                                                }}
                                              >
                                                <td
                                                  style={{
                                                    fontSize: "13px",
                                                    padding: "5px",
                                                    background: "#f9f9f9",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {dialog.uniqueid}
                                                </td>
                                                <td
                                                  style={{
                                                    fontSize: "13px",
                                                    padding: "5px",
                                                    background: "#f9f9f9",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {dialog.buyer_name}
                                                </td>
                                                <td
                                                  style={{
                                                    fontSize: "13px",
                                                    padding: "5px",
                                                    background: "#f9f9f9",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {dialog.buyer_number}
                                                </td>

                                                <td
                                                  style={{
                                                    fontSize: "13px",
                                                    padding: "5px",
                                                    background: "#f9f9f9",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {dialog.actual_timeout}
                                                </td>

                                                <td
                                                  style={{
                                                    fontSize: "13px",
                                                    padding: "5px",
                                                    background: "#f9f9f9",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {dialog.timeout}
                                                </td>

                                                <td
                                                  style={{
                                                    fontSize: "13px",
                                                    padding: "5px",
                                                    background: "#f9f9f9",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {dialog.status}
                                                </td>
                                                <td
                                                  style={{
                                                    fontSize: "13px",
                                                    padding: "5px",
                                                    background: "#f9f9f9",
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {formatDateTimeJSX(
                                                    dialog.created_at,
                                                  )}
                                                </td>
                                              </tr>
                                            ),
                                          )}
                                      </tbody>
                                    </table>
                                  </div>
                                </Box> */}
                              </Grid>
                            </Grid>

                            {/* 🔹 Detail Table */}

                            {/* 🔹 Recording */}
                          </>
                        )}
                      </DialogContent>
                    </Dialog>
                    )}

                    {isAdminOrSuper && (
                      <AdminCdrDetailsModal
                        open={openModal}
                        onClose={handleCloseModal}
                        uniqueid={selectedRow?.uniqueid}
                        colorThem={colorThem}
                        audioControls={{
                          registerAudioRef: (src, el) => {
                            if (el) audioRefs.current[src] = el;
                          },
                          handleAudioClick: (src) => {
                            const el = audioRefs.current[src];
                            if (!el) return;
                            if (currentAudio && currentAudio !== el) currentAudio.pause();
                            setCurrentAudio(el);
                            el.play();
                          },
                          handleAudioPause: () => setCurrentAudio(null),
                        }}
                      />
                    )}

                    {/* Status codes info dialog — opens from the (i) button
                        in the Status column header. Same content/structure
                        as the user portal report. */}
                    <Dialog
                      open={statusInfoOpen}
                      onClose={() => setStatusInfoOpen(false)}
                      maxWidth="md"
                      fullWidth
                    >
                      <DialogTitle
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          pr: 1,
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <InfoOutlinedIcon color="primary" />
                          <Typography variant="h6" fontWeight={700}>
                            Call Status codes
                          </Typography>
                        </Stack>
                        <IconButton size="small" onClick={() => setStatusInfoOpen(false)}>
                          <CloseIcon />
                        </IconButton>
                      </DialogTitle>
                      <DialogContent dividers>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          The <b>Status</b> column shows the final outcome code written by
                          the call-routing engine. Every code rolls up into one of three
                          <b> Call Status </b> categories: <b>ANSWERED</b>, <b>MISSED</b>, or
                          <b> FAILED</b>.
                        </Typography>

                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#047857", mb: 0.5 }}>
                          ANSWERED — the call was picked up
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, "& li": { mb: 0.5 }, fontSize: "0.875rem", mt: 0 }}>
                          <li><code>BUYER_ANSWERED</code> — a buyer in the DID's campaign picked up the forwarded call.</li>
                        </Box>

                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#d97706", mt: 2, mb: 0.5 }}>
                          MISSED — call reached the routing layer but nobody answered
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, "& li": { mb: 0.5 }, fontSize: "0.875rem", mt: 0 }}>
                          <li><code>BUYER_BUSY</code> — buyer's line was busy (SIP 486/600).</li>
                          <li><code>BUYER_NOANSWER</code> — buyer didn't answer within the campaign's ring timeout.</li>
                          <li><code>BUYER_NOT_FOUND</code> — no active buyer was available in the campaign (all at daily limit or disabled).</li>
                          <li><code>CALLER_CANCEL</code> — the caller hung up before any buyer answered.</li>
                          <li><code>COMPAIGN_USER_MISMATCH</code> — the DID's campaign doesn't belong to the DID's owner (configuration error).</li>
                          <li><code>DID_COMPAIGN_MISSING</code> — the DID has no campaign assigned.</li>
                          <li><code>NON_OFFICE_HOUR</code> — call came outside the campaign's working-hours window.</li>
                          <li><code>NOT_SUFFICIENT_FUNDS</code> — user's account balance is below the minimum; call was rejected before dialing.</li>
                        </Box>

                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#b91c1c", mt: 2, mb: 0.5 }}>
                          FAILED — call was rejected or blocked outright
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, "& li": { mb: 0.5 }, fontSize: "0.875rem", mt: 0 }}>
                          <li><code>CALLERID_BLOCKED_BY_USER</code> — caller's number is on the DID owner's block list.</li>
                          <li><code>CONGESTION</code> — trunk or network congestion.</li>
                          <li><code>DID_NOT_ACTIVE</code> — the DID is deactivated.</li>
                          <li><code>DID_SUSPENDED</code> — the DID has been suspended by admin.</li>
                          <li><code>DID_USER_NOT_ACTIVE</code> — the user who owns this DID is inactive.</li>
                          <li><code>IVR_AUTHENDICATION_FAILED</code> — caller entered the wrong PIN (or no PIN) at the IVR auth prompt.</li>
                          <li><code>TRIED_NO_SUCCESS</code> — all buyers in the campaign were tried, none answered. Distinct from <code>BUYER_NOANSWER</code> (single buyer) — this is the whole campaign exhausted.</li>
                          <li><code>USER_NOT_FOUND</code> — the DID lookup didn't return any owner.</li>
                        </Box>

                        <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
                          Occasional edge-case codes (e.g. <code>NO_CARRIERS</code>,
                          <code> BILLING_INFO_NOT_FOUND</code>,
                          <code> CALLERID_VOIP_REJCTION</code>,
                          <code> SHAKEN_VALIDATION_FAILED</code>) may also appear. Contact support if you see those frequently.
                        </Typography>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => setStatusInfoOpen(false)} size="small" variant="contained">
                          Close
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Box>
      </div>
    </div>
  );
}

export default AdminReport;
